/**
 * Centralized API configuration
 * All API endpoints and settings are defined here for easy management
 */

export interface ApiEndpoints {
  auth: {
    login: string;
    refresh: string;
    validateToken: string;
    // Note: Console auth API doesn't provide logout endpoint
  };
  userManagement: {
    base: string;
    list: string;
    create: string;
    validate: string;
    update: string;
    delete: string;
    roles: string;
  };
  userOnboarding: {
    base: string;
    list: string;
    /** CSV export of cases matching the list filters (no pagination). */
    listExport: string;
    detail: string;
    /**
     * Case create — form metadata + submit. Under multi-workflow
     * support, callers append `/:workflow_abv` for non-ONB workflows
     * (e.g. `/create/CR`). The bare path defaults to `ONB` on the
     * server for backward compatibility.
     */
    create: string;
    /**
     * List every workflow registered for candidate cases (`ONB`, `CR`,
     * anything the backend registers later). Response drives the
     * workflow filter + Start picker on the cases list page.
     */
    workflows: string;
    /**
     * States for a given workflow — append `/:workflow_abv/states`.
     * Response drives the status dropdown on the cases list page,
     * reloading whenever the selected workflow changes.
     */
    workflowStatesBase: string;
    /**
     * Invite-mounted workflow driver. Append `/:entity_id` for the
     * current-state / next-events fetch and `/:entity_id/:event` for
     * event metadata (GET) or submit (POST). Accepts both invite
     * tokens and FusionAuth JWTs — replaces the older
     * `/workflow/ONB/*` endpoints that now return `ROUTE_DISABLED`
     * for candidate cases.
     */
    inviteWorkflow: string;
    /** JWT-mounted form router; append `/:form_name/existing_submission` and identifier query params. */
    existingSubmission: string;
    /** Invite-mounted form router; append `/:form_name/existing_submission` and identifier query params. */
    inviteExistingSubmission: string;
    /** Invite-only location cascade; append `/:district?/:subdistrict?`. */
    inviteLocation: string;
    /**
     * Versioned candidate-document API (PAN / Aadhaar / etc.). Append
     * `/:entity_type/:entity_id/upload` or `/:entity_type/:entity_id/version/:version_id`.
     * Both FusionAuth JWT and invite-token auth modes are accepted.
     */
    inviteDocument: string;
    /** POST a list of case ids to push those candidates to SharePoint. */
    sharepointSync: string;
  };
  utilities: {
    base: string;
    options: string;
  };
  assetManagement: {
    base: string;
    impact: {
      base: string;
      upload: string;
      list: string;
      brandfolder: {
        list: string;
      };
    };
    gallery: {
      base: string;
      list: string;
      details: string;
      options: string;
      create: string;
      update: string;
    };
  };
  invoice: {
    base: string;
    pnc: {
      list: string;
      update: string;
      lock: string;
    };
    bu: {
      list: string;
      update: string;
      lock: string;
    };
    finance: {
      list: string;
      generate: string;
    };
  };
  review: {
    /** `/console/review` root. */
    base: string;
    /** GET — resources the current user may review (per JWT roles). */
    resources: string;
  };
  location: {
    base: string;
    list: string;
    districts: string;
    states: string;
  };
  /**
   * Backoffice location-management API — cascade browse, text search,
   * per-id lookup, and (ADMIN-only) custom-location writes. See
   * `docs/location-tracking/location-management.md`.
   */
  consoleLocation: {
    /** `/console/location` — GET returns districts as `string[]`. */
    base: string;
    /** Paginated search: `?q=&district=&subdistrict=&page=&page_size=`. */
    search: string;
    /** Per-id lookup: append `/:lgd_code` (e.g. `V_40034`, `C_100`). */
    village: string;
    /**
     * Custom location create (ADMIN): append
     * `/:district/:subdistrict/:customLocation` (URL-encoded).
     */
    customLocation: string;
    /** ADMIN cache refresh — kicks off async rebuild. */
    cache: string;
  };
  tracker: {
    userActivity: string;
    tracking: string;
    userList: string;
  };
  audit: {
    base: string;
    logs: string;
    userActivity: string;
  };
  settings: {
    base: string;
    system: string;
    user: string;
  };
}

export const apiConfig = {
  // Base configuration
  baseURL: import.meta.env.VITE_API_BASE_URL || 'https://api.rgf.com',
  trackerBaseURL: import.meta.env.VITE_TRACKER_API_URL || '/supabase-fn',
  /** S3 base for NocoDB attachment relative paths. Set `''` when backend hardcodes. */
  assetBaseURL:
    import.meta.env.VITE_ASSET_BASE_URL ||
    'https://prod-rgf-global-nocodb.s3.us-west-2.amazonaws.com/',
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '10000'),
  
  // Request configuration
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  
  // Retry configuration
  retry: {
    attempts: 3,
    delay: 1000, // milliseconds
  },
  
  // API Endpoints based on existing documentation
  endpoints: {
    // Authentication endpoints (updated for console)
    // Note: Console auth API only provides login, refresh, and me endpoints
    auth: {
      login: '/console/auth/login',
      refresh: '/console/auth/refresh', 
      validateToken: '/console/auth/me'
    },
    
    // User Management endpoints (from API documentation)
    userManagement: {
      base: '/console/user_management',
      list: '/console/user_management/list',
      create: '/console/user_management/create',
      validate: '/console/user_management/create/validate',
      update: '/console/user_management/update',
      delete: '/console/user_management/delete',
      roles: '/console/user_management/application_roles'
    },

    // User Onboarding endpoints — hosts candidate cases across every
    // registered workflow (ONB / CR / …). Legacy segment name kept
    // per the new doc's "future base-path rename" note.
    userOnboarding: {
      base: '/console/user_onboarding',
      list: '/console/user_onboarding/list',
      listExport: '/console/user_onboarding/list/export',
      detail: '/console/user_onboarding/list', // /:id appended at call site
      // Bare `/create` defaults to ONB on the server. Callers can
      // append `/:workflow_abv` for non-ONB workflows (e.g. `/CR`).
      create: '/console/user_onboarding/create',
      // Multi-workflow registry endpoints.
      workflows: '/console/user_onboarding/workflows',
      workflowStatesBase: '/console/user_onboarding/workflows', // /:workflow_abv/states appended at call site
      // Invite-mounted workflow driver — replaces `/workflow/ONB/*`
      // for candidate cases. Accepts JWT + invite token.
      // `${inviteWorkflow}/${entity_id}` or `${inviteWorkflow}/${entity_id}/${event}`.
      inviteWorkflow: '/console/user_onboarding/invite/workflow',
      // `${existingSubmission}/${form_name}/existing_submission?<identifier>=...`
      existingSubmission: '/console/user_onboarding/form',
      inviteExistingSubmission: '/console/user_onboarding/invite/form',
      // `${inviteLocation}` or `${inviteLocation}/${district}/${subdistrict}`
      inviteLocation: '/console/user_onboarding/invite/location',
      // `${inviteDocument}/${entity_type}/${entity_id}/upload`
      // `${inviteDocument}/${entity_type}/${entity_id}/version/${version_id}`
      inviteDocument: '/console/user_onboarding/invite/document',
      sharepointSync: '/console/user_onboarding/sharepoint/sync',
    },


    // Utilities (search-backed options used by the form-builder Search widget)
    utilities: {
      base: '/utilities',
      options: '/utilities/options'
    },
    
    // Asset Management endpoints
    assetManagement: {
      base: '/console/asset_management',
      impact: {
        base: '/console/asset_management/impact',
        upload: '/console/asset_management/impact/upload',
        list: '/console/asset_management/impact/list',
        brandfolder: {
          list: '/console/asset_management/impact/list/brandfolder'
        }
      },
      library: {
        base: '/console/asset_management/library',
        list: '/console/asset_management/library/list',
        getById: '/console/asset_management/library',
        update: '/console/asset_management/library'
      },
      iconik: {
        base: '/console/asset_management/iconik',
        preview: '/console/asset_management/iconik/preview'
      },
      gallery: {
        base: '/console/asset_management/gallery',
        list: '/console/asset_management/gallery/list',
        details: '/console/asset_management/gallery/list', // /:gallery_id will be appended
        options: '/console/asset_management/gallery/options', // /:gallery_id will be appended
        create: '/console/asset_management/gallery/create',
        update: '/console/asset_management/gallery/update'
      }
    },
    
    // Invoice Management endpoints (section-based)
    invoice: {
      base: '/console/invoice',
      // Section-based endpoints (will be dynamically constructed)
      pnc: {
        list: '/console/invoice/pnc/list',
        update: '/console/invoice/pnc/update',
        lock: '/console/invoice/pnc/lock',
        export: '/console/invoice/pnc/list/export'
      },
      bu: {
        list: '/console/invoice/bu/list',
        update: '/console/invoice/bu/update',
        lock: '/console/invoice/bu/lock',
        export: '/console/invoice/bu/list/export'
      },
      finance: {
        list: '/console/invoice/finance/list',
        update: '/console/invoice/finance/update',
        generate: '/console/invoice/finance/generate',
        export: '/console/invoice/finance/list/export'
      }
    },
    
    // Resource-review endpoints — maker-checker system for data collected
    // by the Impact app. The paths for per-resource operations are built
    // at call sites: `${base}/${resource}/meta`,
    // `${base}/${resource}/submissions`,
    // `${base}/${resource}/submissions/${id}`,
    // `${base}/${resource}/submissions/${id}/changelog`,
    // `${base}/${resource}/submissions/${id}/restore`.
    review: {
      base: '/console/review',
      resources: '/console/review/resources',
    },

    // Location endpoints
    location: {
      base: '/location',
      list: '/location/list',
      districts: '/console/user_management/district_options',
      states: '/location/states',
      form: '/form/location'
    },

    // Backoffice location-management API (Punjab master lookup +
    // custom locations). See docs/location-tracking/location-management.md.
    consoleLocation: {
      base: '/console/location',
      search: '/console/location/search',
      village: '/console/location/village',
      customLocation: '/console/location/customLocation',
      cache: '/console/location/cache',
    },

    // Tracker endpoints (Supabase Edge Functions)
    tracker: {
      userActivity: '/user-activity',
      tracking: '/tracking',
      userList: '/user-list',
      backofficeUser: '/user',
    },

    // Audit logging endpoints (future implementation)
    audit: {
      base: '/audit',
      logs: '/audit/logs',
      userActivity: '/audit/user-activity'
    },
    
    // System settings endpoints (future implementation)
    settings: {
      base: '/settings',
      system: '/settings/system',
      user: '/settings/user'
    }
  } as ApiEndpoints,
  
  // Feature flags from environment
  features: {
    enableAuditLogs: import.meta.env.VITE_ENABLE_AUDIT_LOGS === 'true',
    enableBulkOperations: import.meta.env.VITE_ENABLE_BULK_OPERATIONS === 'true',
    enableAdvancedFilters: import.meta.env.VITE_ENABLE_ADVANCED_FILTERS === 'true',
    /**
     * OSRM path-correction workflow on the Live Tracking map. Off by
     * default so the UI never surfaces the right-click menu, status
     * pill, historical OSRM overlays, or preview panel in environments
     * where the Supabase Edge Functions (`/match-route`,
     * `/insert-osrm-path`, `/sync-osrm-data`) aren't deployed yet.
     * Flip to `true` per-env once the backend is live.
     */
    enableOsrmPathCorrection:
      import.meta.env.VITE_ENABLE_OSRM_PATH_CORRECTION === 'true',
    /**
     * Impact Data (maker-checker) review UI under each Program. Off by
     * default so the sidebar's Impact Data sub-item lands on an
     * inert placeholder in environments where the `/console/review/*`
     * endpoints aren't deployed yet. Flip to `true` per-env once the
     * backend is live (RF pilot first, other programs follow).
     */
    enableImpactDataReview:
      import.meta.env.VITE_ENABLE_IMPACT_DATA_REVIEW === 'true',
  },
  
  // Application settings
  app: {
    name: import.meta.env.VITE_APP_NAME || 'RGF Back Office',
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    environment: import.meta.env.VITE_ENVIRONMENT || 'development',
  },

  // Token configuration
  auth: {
    tokenKey: import.meta.env.VITE_AUTH_TOKEN_KEY || 'rgf_auth_token',
    refreshTokenKey: import.meta.env.VITE_AUTH_REFRESH_TOKEN_KEY || 'rgf_refresh_token',
  }
};

// Type-safe endpoint access
export const getEndpoint = (category: keyof ApiEndpoints, endpoint: string): string => {
  const categoryEndpoints = apiConfig.endpoints[category] as Record<string, string>;
  const endpointUrl = categoryEndpoints[endpoint];
  
  if (!endpointUrl) {
    throw new Error(`Endpoint ${category}.${endpoint} not found`);
  }
  
  return endpointUrl;
};

// Helper function to get section-based invoice endpoints
export const getInvoiceEndpoint = (section: 'pnc' | 'bu' | 'finance', operation: 'list' | 'update' | 'lock' | 'generate' | 'export'): string => {
  const sectionEndpoints = apiConfig.endpoints.invoice[section] as Record<string, string>;
  
  if (!sectionEndpoints) {
    throw new Error(`Invoice section ${section} not found`);
  }
  
  const endpointUrl = sectionEndpoints[operation];
  
  if (!endpointUrl) {
    throw new Error(`Invoice endpoint ${section}.${operation} not found or not allowed`);
  }
  
  return endpointUrl;
};

// Helper function to build full URL
export const buildApiUrl = (endpoint: string): string => {
  return `${apiConfig.baseURL}${endpoint}`;
};

export default apiConfig;